import { useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen,
  Castle,
  ChevronRight,
  Download,
  FlaskConical,
  Gem,
  Globe2,
  Landmark,
  LibraryBig,
  Map,
  Network,
  Pencil,
  Plus,
  ScrollText,
  Shield,
  Sparkles,
  Swords,
  Trash2,
  Users,
} from "lucide-react";
import type {
  AppData,
  EntityType,
  ItemCategory,
  ItemDefinition,
  ItemInstance,
  MapAsset,
} from "./types";
import { exportData, loadData, newId, now, saveData } from "./lib/store";
import { seedDemo } from "./lib/seed";
import { Modal } from "./components/Modal";
import { SrdItemImporter } from "./components/SrdItemImporter";
import { CreatureMaster } from "./components/CreatureMaster";
import { appEnvironment } from "./lib/config";

type View =
  | "overview"
  | "npcs"
  | "locations"
  | "factions"
  | "religion"
  | "lore"
  | "itemmaster"
  | "creaturemaster"
  | "items"
  | "maps"
  | "adventures"
  | "relationships"
  | "campaigns";
const NAV: [View, string, any][] = [
  ["overview", "Overview", Globe2],
  ["npcs", "NPCs", Users],
  ["locations", "Locations", Landmark],
  ["factions", "Factions", Shield],
  ["religion", "Religion", Sparkles],
  ["lore", "Rumors & Secrets", ScrollText],
  ["itemmaster", "Item Master", LibraryBig],
  ["creaturemaster", "Creature Master", FlaskConical],
  ["items", "World Items", Gem],
  ["maps", "Maps", Map],
  ["adventures", "Adventure Ideas", Swords],
  ["relationships", "Relationships", Network],
  ["campaigns", "Campaigns", BookOpen],
];
const typesFor = (v: View): EntityType[] =>
  v === "npcs"
    ? ["npc"]
    : v === "locations"
      ? ["location"]
      : v === "factions"
        ? ["faction"]
        : v === "religion"
          ? ["pantheon", "deity"]
          : v === "lore"
            ? ["rumor", "secret"]
            : v === "adventures"
              ? ["adventure"]
              : [];
interface AppProps {
  initialData?: AppData;
  onDataChange?: (data: AppData) => void | Promise<void>;
  persistenceLabel?: string;
  keepLocalBackup?: boolean;
}

const entityLabel = (t: EntityType) =>
  ({
    npc: "NPC",
    location: "Location",
    faction: "Faction",
    pantheon: "Pantheon",
    deity: "Deity",
    rumor: "Rumor",
    secret: "Secret",
    adventure: "Adventure",
    item: "Legacy Item",
  })[t];

export default function App({
  initialData,
  onDataChange,
  persistenceLabel = "Browser storage",
  keepLocalBackup = true,
}: AppProps) {
  const [data, setData] = useState<AppData>(() =>
    initialData ? structuredClone(initialData) : loadData(),
  );
  const [worldId, setWorldId] = useState(data.worlds[0]?.id || "");
  const [view, setView] = useState<View>("overview");
  const [modal, setModal] = useState<
    | null
    | "world"
    | "entity"
    | "relationship"
    | "campaign"
    | "map"
    | "itemDef"
    | "itemInstance"
    | "moveInstance"
    | "srdImport"
  >(null);
  const [preset, setPreset] = useState<EntityType>("npc");
  const [selectedWorldId, setSelectedWorldId] = useState("");
  const [selectedEntityId, setSelectedEntityId] = useState("");
  const [selectedDefId, setSelectedDefId] = useState("");
  const [selectedInstanceId, setSelectedInstanceId] = useState("");
  const [selectedMapId, setSelectedMapId] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [selectedRelationshipId, setSelectedRelationshipId] = useState("");
  const didInitializePersistence = useRef(false);

  useEffect(() => {
    if (keepLocalBackup) {
      saveData(data);
    }

    if (!didInitializePersistence.current) {
      didInitializePersistence.current = true;
      return;
    }

    void onDataChange?.(data);
  }, [data, keepLocalBackup, onDataChange]);
  useEffect(() => {
    if (data.worlds.length && !data.worlds.some((w) => w.id === worldId))
      setWorldId(data.worlds[0].id);
  }, [data.worlds, worldId]);

  const world = data.worlds.find((w) => w.id === worldId);
  const ents = data.entities.filter((e) => e.worldId === worldId);
  const entityMap = useMemo(
    () => Object.fromEntries(data.entities.map((e) => [e.id, e])),
    [data.entities],
  );
  const defMap = useMemo(
    () => Object.fromEntries(data.itemDefinitions.map((d) => [d.id, d])),
    [data.itemDefinitions],
  );
  const selectedWorld = data.worlds.find((w) => w.id === selectedWorldId);
  const selectedEntity = data.entities.find((e) => e.id === selectedEntityId);
  const selectedDef = data.itemDefinitions.find((d) => d.id === selectedDefId);
  const selectedInstance = data.itemInstances.find((i) => i.id === selectedInstanceId);
  const selectedMap = data.maps.find((m) => m.id === selectedMapId);
  const selectedCampaign = data.campaigns.find((c) => c.id === selectedCampaignId);
  const selectedRelationship = data.relationships.find((r) => r.id === selectedRelationshipId);
  const patch = (fn: (d: AppData) => void) =>
    setData((prev) => {
      const d = structuredClone(prev);
      fn(d);
      return d;
    });
  const openEntity = (t: EntityType) => {
    setSelectedEntityId("");
    setPreset(t);
    setModal("entity");
  };
  const editEntity = (id: string) => {
    const e = data.entities.find((x) => x.id === id);
    if (!e) return;
    setSelectedEntityId(id);
    setPreset(e.type);
    setModal("entity");
  };

  function addWorld(f: HTMLFormElement) {
    const fd = new FormData(f),
      id = newId();
    patch((d) =>
      d.worlds.push({
        id,
        name: String(fd.get("name")),
        description: String(fd.get("description") || ""),
        theme: String(fd.get("theme") || ""),
        magicLevel: String(fd.get("magic") || ""),
        system: String(fd.get("system") || "D&D 5e / 2024"),
        createdAt: now(),
      }),
    );
    setWorldId(id);
    setModal(null);
  }
  function updateWorld(f: HTMLFormElement) {
    if (!selectedWorldId) return;
    const fd = new FormData(f);
    patch((d) => {
      const w = d.worlds.find((x) => x.id === selectedWorldId);
      if (!w) return;
      w.name = String(fd.get("name"));
      w.description = String(fd.get("description") || "");
      w.theme = String(fd.get("theme") || "");
      w.magicLevel = String(fd.get("magic") || "");
      w.system = String(fd.get("system") || "D&D 5e / 2024");
    });
    setSelectedWorldId("");
    setModal(null);
  }
  function deleteWorld(id: string) {
    const w = data.worlds.find((x) => x.id === id);
    if (!w) return;
    const refs =
      data.entities.filter((x) => x.worldId === id).length +
      data.relationships.filter((x) => x.worldId === id).length +
      data.campaigns.filter((x) => x.worldId === id).length +
      data.maps.filter((x) => x.worldId === id).length +
      data.itemInstances.filter((x) => x.worldId === id).length;
    if (refs) {
      window.alert(
        `Cannot delete "${w.name}" while it still contains world records. Remove its entities, relationships, campaigns, maps and world items first.`,
      );
      return;
    }
    if (!window.confirm(`Permanently delete world "${w.name}"? This cannot be undone.`)) return;
    patch((d) => {
      d.worlds = d.worlds.filter((x) => x.id !== id);
    });
    setSelectedWorldId("");
    setModal(null);
  }
  function addEntity(f: HTMLFormElement) {
    const fd = new FormData(f),
      type = String(fd.get("type")) as EntityType,
      details: Record<string, unknown> = {};
    const a = fd.get("a"),
      b = fd.get("b");
    if (type === "npc") {
      details.species = a;
      details.occupation = b;
    }
    if (type === "location") {
      details.locationType = a;
      details.terrain = b;
    }
    if (type === "faction") {
      details.factionType = a;
      details.purpose = b;
    }
    if (type === "pantheon") {
      details.creationMyth = a;
      details.cosmology = b;
    }
    if (type === "deity") {
      details.portfolio = a;
      details.symbol = b;
    }
    if (type === "rumor") {
      details.truthStatus = a;
      details.truthExplanation = b;
    }
    if (type === "secret") {
      details.secretType = a;
      details.importance = Number(b || 3);
    }
    if (type === "adventure") {
      details.adventureType = a;
      details.objective = b;
    }
    patch((d) =>
      d.entities.push({
        id: newId(),
        worldId,
        type,
        name: String(fd.get("name")),
        description: String(fd.get("description") || ""),
        visibility: String(fd.get("visibility") || "gm_only") as any,
        tags: String(fd.get("tags") || "")
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean),
        details,
        createdAt: now(),
      }),
    );
    setModal(null);
  }
  function updateEntity(f: HTMLFormElement) {
    if (!selectedEntityId) return;
    const fd = new FormData(f),
      type = String(fd.get("type")) as EntityType,
      details: Record<string, unknown> = {};
    const a = fd.get("a"),
      b = fd.get("b");
    if (type === "npc") {
      details.species = a;
      details.occupation = b;
    }
    if (type === "location") {
      details.locationType = a;
      details.terrain = b;
    }
    if (type === "faction") {
      details.factionType = a;
      details.purpose = b;
    }
    if (type === "pantheon") {
      details.creationMyth = a;
      details.cosmology = b;
    }
    if (type === "deity") {
      details.portfolio = a;
      details.symbol = b;
    }
    if (type === "rumor") {
      details.truthStatus = a;
      details.truthExplanation = b;
    }
    if (type === "secret") {
      details.secretType = a;
      details.importance = Number(b || 3);
    }
    if (type === "adventure") {
      details.adventureType = a;
      details.objective = b;
    }
    patch((d) => {
      const e = d.entities.find((x) => x.id === selectedEntityId);
      if (!e) return;
      e.name = String(fd.get("name"));
      e.description = String(fd.get("description") || "");
      e.visibility = String(fd.get("visibility") || "gm_only") as any;
      e.tags = String(fd.get("tags") || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      e.details = details;
    });
    setSelectedEntityId("");
    setModal(null);
  }
  function deleteEntity(id: string) {
    const e = data.entities.find((x) => x.id === id);
    if (!e) return;
    const rels = data.relationships.filter((r) => r.sourceId === id || r.targetId === id).length;
    const maps = data.maps.filter((m) => m.linkedEntityId === id).length;
    const items = data.itemInstances.filter(
      (i) =>
        i.currentOwnerEntityId === id ||
        i.currentHolderEntityId === id ||
        i.currentLocationEntityId === id,
    ).length;
    if (rels || maps || items) {
      window.alert(
        `Cannot delete "${e.name}" yet. It is referenced by ${rels} relationship(s), ${maps} map(s), and ${items} world item(s). Remove those references first.`,
      );
      return;
    }
    if (!window.confirm(`Permanently delete "${e.name}"? This cannot be undone.`)) return;
    patch((d) => {
      d.entities = d.entities.filter((x) => x.id !== id);
      d.campaignState = d.campaignState.filter((x) => x.entityId !== id);
      d.campaignEvents = d.campaignEvents.filter(
        (x) => x.entityId !== id && x.relatedEntityId !== id,
      );
    });
  }
  function addRelationship(f: HTMLFormElement) {
    const fd = new FormData(f);
    patch((d) =>
      d.relationships.push({
        id: newId(),
        worldId,
        sourceId: String(fd.get("source")),
        targetId: String(fd.get("target")),
        type: String(fd.get("type")).toUpperCase().replaceAll(" ", "_"),
        description: String(fd.get("description") || ""),
        secret: fd.get("secret") === "on",
        createdAt: now(),
      }),
    );
    setModal(null);
  }
  function updateRelationship(f: HTMLFormElement) {
    if (!selectedRelationshipId) return;
    const fd = new FormData(f);
    patch((d) => {
      const r = d.relationships.find((x) => x.id === selectedRelationshipId);
      if (!r) return;
      r.sourceId = String(fd.get("source"));
      r.targetId = String(fd.get("target"));
      r.type = String(fd.get("type")).toUpperCase().replaceAll(" ", "_");
      r.description = String(fd.get("description") || "");
      r.secret = fd.get("secret") === "on";
    });
    setSelectedRelationshipId("");
    setModal(null);
  }
  function addCampaign(f: HTMLFormElement) {
    const fd = new FormData(f);
    patch((d) =>
      d.campaigns.push({
        id: newId(),
        worldId,
        name: String(fd.get("name")),
        description: String(fd.get("description") || ""),
        createdAt: now(),
      }),
    );
    setModal(null);
  }
  function updateCampaign(f: HTMLFormElement) {
    const fd = new FormData(f);
    patch((d) => {
      const c = d.campaigns.find((x) => x.id === selectedCampaignId);
      if (!c) return;
      c.name = String(fd.get("name"));
      c.description = String(fd.get("description") || "");
    });
    setSelectedCampaignId("");
    setModal(null);
  }
  function deleteCampaign(id: string) {
    const c = data.campaigns.find((x) => x.id === id);
    if (
      !c ||
      !window.confirm(
        `Permanently delete campaign "${c.name}" and its campaign state/events? This cannot be undone.`,
      )
    )
      return;
    patch((d) => {
      d.campaigns = d.campaigns.filter((x) => x.id !== id);
      d.campaignState = d.campaignState.filter((x) => x.campaignId !== id);
      d.campaignEvents = d.campaignEvents.filter((x) => x.campaignId !== id);
      d.itemMovements = d.itemMovements.filter((x) => x.campaignId !== id);
    });
  }
  function addMap(f: HTMLFormElement) {
    const fd = new FormData(f);
    const m: MapAsset = {
      id: newId(),
      worldId,
      name: String(fd.get("name")),
      mapType: String(fd.get("mapType")),
      linkedEntityId: String(fd.get("linked") || "") || undefined,
      gmUrl: String(fd.get("gmUrl") || "") || undefined,
      playerUrl: String(fd.get("playerUrl") || "") || undefined,
      vttUrl: String(fd.get("vttUrl") || "") || undefined,
      gridType: String(fd.get("gridType") || "") || undefined,
      gridSize: Number(fd.get("gridSize") || 0) || undefined,
      notes: String(fd.get("notes") || ""),
      createdAt: now(),
    };
    patch((d) => d.maps.push(m));
    setModal(null);
  }
  function updateMap(f: HTMLFormElement) {
    const fd = new FormData(f);
    patch((d) => {
      const m = d.maps.find((x) => x.id === selectedMapId);
      if (!m) return;
      m.name = String(fd.get("name"));
      m.mapType = String(fd.get("mapType"));
      m.linkedEntityId = String(fd.get("linked") || "") || undefined;
      m.gmUrl = String(fd.get("gmUrl") || "") || undefined;
      m.playerUrl = String(fd.get("playerUrl") || "") || undefined;
      m.vttUrl = String(fd.get("vttUrl") || "") || undefined;
      m.gridType = String(fd.get("gridType") || "") || undefined;
      m.gridSize = Number(fd.get("gridSize") || 0) || undefined;
      m.notes = String(fd.get("notes") || "");
    });
    setSelectedMapId("");
    setModal(null);
  }
  function deleteMap(id: string) {
    const m = data.maps.find((x) => x.id === id);
    if (!m || !window.confirm(`Permanently delete map "${m.name}"? This cannot be undone.`)) return;
    patch((d) => {
      d.maps = d.maps.filter((x) => x.id !== id);
    });
  }
  function addItemDefinition(f: HTMLFormElement) {
    const fd = new FormData(f),
      t = now();
    const obj: ItemDefinition = {
      id: newId(),
      workspaceScope: "local",
      name: String(fd.get("name")),
      category: String(fd.get("category")) as ItemCategory,
      subtype: String(fd.get("subtype") || "") || undefined,
      rarity: String(fd.get("rarity") || "") || undefined,
      magical: fd.get("magical") === "on",
      requiresAttunement: fd.get("attune") === "on",
      attunementRequirements: String(fd.get("attunementRequirements") || "") || undefined,
      consumable: fd.get("consumable") === "on",
      charges: Number(fd.get("charges") || 0) || undefined,
      weight: Number(fd.get("weight") || 0) || undefined,
      valueGp: Number(fd.get("valueGp") || 0) || undefined,
      source: String(fd.get("source") || "") || undefined,
      rulesVersion: String(fd.get("rulesVersion") || "D&D 5e / 2024"),
      summary: String(fd.get("summary") || ""),
      mechanics: String(fd.get("mechanics") || ""),
      lore: String(fd.get("lore") || ""),
      gmSecrets: String(fd.get("gmSecrets") || ""),
      imageUrl: String(fd.get("imageUrl") || "") || undefined,
      tags: String(fd.get("tags") || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean),
      createdAt: t,
      updatedAt: t,
    };
    patch((d) => d.itemDefinitions.push(obj));
    setModal(null);
  }
  function updateItemDefinition(f: HTMLFormElement) {
    if (!selectedDefId) return;
    const fd = new FormData(f),
      t = now();
    patch((d) => {
      const obj = d.itemDefinitions.find((x) => x.id === selectedDefId);
      if (!obj) return;
      obj.name = String(fd.get("name"));
      obj.category = String(fd.get("category")) as ItemCategory;
      obj.subtype = String(fd.get("subtype") || "") || undefined;
      obj.rarity = String(fd.get("rarity") || "") || undefined;
      obj.magical = fd.get("magical") === "on";
      obj.requiresAttunement = fd.get("attune") === "on";
      obj.attunementRequirements = String(fd.get("attunementRequirements") || "") || undefined;
      obj.consumable = fd.get("consumable") === "on";
      obj.charges = Number(fd.get("charges") || 0) || undefined;
      obj.weight = Number(fd.get("weight") || 0) || undefined;
      obj.valueGp = Number(fd.get("valueGp") || 0) || undefined;
      obj.source = String(fd.get("source") || "") || undefined;
      obj.rulesVersion = String(fd.get("rulesVersion") || "D&D 5e / 2024");
      obj.summary = String(fd.get("summary") || "");
      obj.mechanics = String(fd.get("mechanics") || "");
      obj.lore = String(fd.get("lore") || "");
      obj.gmSecrets = String(fd.get("gmSecrets") || "");
      obj.imageUrl = String(fd.get("imageUrl") || "") || undefined;
      obj.tags = String(fd.get("tags") || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      obj.updatedAt = t;
    });
    setSelectedDefId("");
    setModal(null);
  }
  function deleteItemDefinition(id: string) {
    const obj = data.itemDefinitions.find((x) => x.id === id);
    if (!obj) return;
    const count = data.itemInstances.filter((i) => i.definitionId === id).length;
    if (count) {
      window.alert(
        `Cannot delete "${obj.name}". ${count} world item instance(s) currently use this definition. Delete those instances first.`,
      );
      return;
    }
    if (!window.confirm(`Permanently delete item definition "${obj.name}"? This cannot be undone.`))
      return;
    patch((d) => {
      d.itemDefinitions = d.itemDefinitions.filter((x) => x.id !== id);
    });
  }
  function addItemInstance(f: HTMLFormElement) {
    const fd = new FormData(f),
      t = now();
    const obj: ItemInstance = {
      id: newId(),
      worldId,
      definitionId: String(fd.get("definitionId")),
      displayName: String(fd.get("displayName") || "") || undefined,
      uniqueItem: fd.get("uniqueItem") === "on",
      quantity: Math.max(1, Number(fd.get("quantity") || 1)),
      condition: String(fd.get("condition") || "Good"),
      identified: fd.get("identified") === "on",
      currentOwnerEntityId: String(fd.get("owner") || "") || undefined,
      currentHolderEntityId: String(fd.get("holder") || "") || undefined,
      currentLocationEntityId: String(fd.get("location") || "") || undefined,
      notes: String(fd.get("notes") || ""),
      createdAt: t,
      updatedAt: t,
    };
    patch((d) => d.itemInstances.push(obj));
    setModal(null);
  }
  function updateItemInstance(f: HTMLFormElement) {
    if (!selectedInstanceId) return;
    const fd = new FormData(f);
    patch((d) => {
      const obj = d.itemInstances.find((x) => x.id === selectedInstanceId);
      if (!obj) return;
      obj.definitionId = String(fd.get("definitionId"));
      obj.displayName = String(fd.get("displayName") || "") || undefined;
      obj.uniqueItem = fd.get("uniqueItem") === "on";
      obj.quantity = Math.max(1, Number(fd.get("quantity") || 1));
      obj.condition = String(fd.get("condition") || "Good");
      obj.identified = fd.get("identified") === "on";
      obj.currentOwnerEntityId = String(fd.get("owner") || "") || undefined;
      obj.currentHolderEntityId = String(fd.get("holder") || "") || undefined;
      obj.currentLocationEntityId = String(fd.get("location") || "") || undefined;
      obj.notes = String(fd.get("notes") || "");
      obj.updatedAt = now();
    });
    setSelectedInstanceId("");
    setModal(null);
  }
  function deleteItemInstance(id: string) {
    const i = data.itemInstances.find((x) => x.id === id);
    if (!i) return;
    const name = i.displayName || defMap[i.definitionId]?.name || "this item";
    if (
      !window.confirm(
        `Permanently delete "${name}" and its movement history? This cannot be undone.`,
      )
    )
      return;
    patch((d) => {
      d.itemInstances = d.itemInstances.filter((x) => x.id !== id);
      d.itemMovements = d.itemMovements.filter((x) => x.itemId !== id);
    });
  }
  function moveInstance(f: HTMLFormElement) {
    const fd = new FormData(f),
      instanceId = String(fd.get("instanceId")),
      campaignId = String(fd.get("campaign") || "") || undefined;
    const to = String(fd.get("to") || "") || undefined,
      kind = String(fd.get("kind") || "moved");
    patch((d) => {
      const inst = d.itemInstances.find((i) => i.id === instanceId);
      if (!inst) return;
      const from = inst.currentHolderEntityId || inst.currentLocationEntityId;
      if (kind === "stored") {
        inst.currentLocationEntityId = to;
        inst.currentHolderEntityId = undefined;
      } else if (
        kind === "given" ||
        kind === "loaned" ||
        kind === "stolen" ||
        kind === "recovered"
      ) {
        inst.currentHolderEntityId = to;
        inst.currentLocationEntityId = undefined;
      } else if (kind === "destroyed") {
        inst.condition = "Destroyed";
        inst.currentHolderEntityId = undefined;
        inst.currentLocationEntityId = undefined;
      } else {
        inst.currentLocationEntityId = to;
      }
      inst.updatedAt = now();
      d.itemMovements.push({
        id: newId(),
        campaignId,
        itemId: instanceId,
        fromEntityId: from,
        toEntityId: to,
        movementType: kind,
        notes: String(fd.get("notes") || ""),
        createdAt: now(),
      });
      if (campaignId)
        d.campaignEvents.push({
          id: newId(),
          campaignId,
          type: "ITEM_MOVED",
          entityId: undefined,
          relatedEntityId: to,
          description: `${inst.displayName || defMap[inst.definitionId]?.name || "Item"}: ${kind}${to ? ` → ${entityMap[to]?.name || "destination"}` : ""}.`,
          createdAt: now(),
        });
    });
    setModal(null);
  }

  const visible = ents.filter((e) => typesFor(view).includes(e.type));
  const worldInstances = data.itemInstances.filter((i) => i.worldId === worldId);

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">✦</div>
          <div>
            <b>WORLDWARDEN</b>
            <span>STARFALL WORKS</span>
            {appEnvironment !== "production" && appEnvironment !== "local" && (
              <span className="environment-badge">
                {appEnvironment === "development" ? "DEV / BETA" : appEnvironment.toUpperCase()}
              </span>
            )}
          </div>
        </div>
        <div className="world-switch">
          <label>Current world</label>
          <select value={worldId} onChange={(e) => setWorldId(e.target.value)}>
            <option value="">No world selected</option>
            {data.worlds.map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </select>
        </div>
        <nav>
          {NAV.map(([id, l, Icon]) => (
            <button key={id} className={view === id ? "active" : ""} onClick={() => setView(id)}>
              <Icon size={17} />
              {l}
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <button onClick={() => exportData(data)}>
            <Download size={16} />
            Backup JSON
          </button>
          <span>Worldwarden v0.6</span>
          <span>{persistenceLabel}</span>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div>
            <div className="eyebrow">STARFALL WORKS / WORLDWARDEN</div>
            <h1>{world?.name || "Build worlds worth remembering."}</h1>
            <p>
              {world?.description ||
                "Create the canonical lore Storywarden will later bring to life at the table."}
            </p>
          </div>
          <div className="top-actions">
            {!data.worlds.length && (
              <button
                className="secondary"
                onClick={() => {
                  const d = seedDemo();
                  setData(d);
                  setWorldId(d.worlds[0].id);
                }}
              >
                Load Demo
              </button>
            )}
            {world && (
              <button
                className="secondary"
                onClick={() => {
                  setSelectedWorldId(world.id);
                  setModal("world");
                }}
              >
                <Pencil size={16} />
                Edit World
              </button>
            )}
            <button
              className="primary"
              onClick={() => {
                setSelectedWorldId("");
                setModal("world");
              }}
            >
              <Plus size={16} />
              New World
            </button>
          </div>
        </header>

        {!world ? (
          <Welcome
            onCreate={() => setModal("world")}
            onDemo={() => {
              const d = seedDemo();
              setData(d);
              setWorldId(d.worlds[0].id);
            }}
          />
        ) : (
          <>
            {view === "overview" && <Overview data={data} worldId={worldId} />}
            {["npcs", "locations", "factions", "religion", "lore", "adventures"].includes(view) && (
              <section>
                <SectionHead
                  title={NAV.find((n) => n[0] === view)?.[1] || ""}
                  subtitle={
                    view === "lore"
                      ? "Canonical truths, incomplete stories and things players may discover later."
                      : "Canonical world content, safe from campaign-specific changes."
                  }
                  onAdd={() =>
                    openEntity(
                      view === "npcs"
                        ? "npc"
                        : view === "locations"
                          ? "location"
                          : view === "factions"
                            ? "faction"
                            : view === "religion"
                              ? "deity"
                              : view === "lore"
                                ? "secret"
                                : "adventure",
                    )
                  }
                />
                {!visible.length ? (
                  <Empty />
                ) : (
                  <div className="card-grid">
                    {visible.map((e) => (
                      <article className="entity-card" key={e.id}>
                        <div className="entity-top">
                          <span>{entityLabel(e.type)}</span>
                          <span>{e.visibility.replace("_", " ")}</span>
                        </div>
                        <h3>{e.name}</h3>
                        <p>{e.description || "No description yet."}</p>
                        <div className="tags">
                          {e.tags.map((t) => (
                            <span key={t}>{t}</span>
                          ))}
                        </div>
                        <div className="card-actions">
                          <button className="secondary compact" onClick={() => editEntity(e.id)}>
                            <Pencil size={14} />
                            Edit
                          </button>
                          <button className="danger compact" onClick={() => deleteEntity(e.id)}>
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
                {view === "religion" && (
                  <div className="sub-actions">
                    <button className="secondary" onClick={() => openEntity("pantheon")}>
                      + Pantheon
                    </button>
                    <button className="secondary" onClick={() => openEntity("deity")}>
                      + Deity
                    </button>
                  </div>
                )}
                {view === "lore" && (
                  <div className="sub-actions">
                    <button className="secondary" onClick={() => openEntity("rumor")}>
                      + Rumor
                    </button>
                    <button className="secondary" onClick={() => openEntity("secret")}>
                      + Secret
                    </button>
                  </div>
                )}
              </section>
            )}

            {view === "itemmaster" && (
              <section>
                <div className="section-head">
                  <div>
                    <h2>Item Master</h2>
                    <p>
                      A reusable catalog of item definitions. Create homebrew items or import open
                      SRD items, then place copies or unique instances into any world.
                    </p>
                  </div>
                  <div className="top-actions">
                    <button className="secondary" onClick={() => setModal("srdImport")}>
                      <Download size={16} />
                      Import from SRD
                    </button>
                    <button
                      className="primary"
                      onClick={() => {
                        setSelectedDefId("");
                        setModal("itemDef");
                      }}
                    >
                      <Plus size={16} />
                      Create Homebrew
                    </button>
                  </div>
                </div>
                <div className="item-master-stats">
                  <div>
                    <b>{data.itemDefinitions.length}</b>
                    <span>Definitions</span>
                  </div>
                  <div>
                    <b>{data.itemDefinitions.filter((i) => i.magical).length}</b>
                    <span>Magical</span>
                  </div>
                  <div>
                    <b>{data.itemDefinitions.filter((i) => i.requiresAttunement).length}</b>
                    <span>Attunement</span>
                  </div>
                  <div>
                    <b>{data.itemDefinitions.filter((i) => i.consumable).length}</b>
                    <span>Consumables</span>
                  </div>
                </div>
                {!data.itemDefinitions.length ? (
                  <Empty />
                ) : (
                  <div className="item-table">
                    <div className="item-table-head">
                      <span>Name</span>
                      <span>Category</span>
                      <span>Rarity</span>
                      <span>Rules</span>
                      <span></span>
                    </div>
                    {data.itemDefinitions.map((d) => (
                      <div className="item-table-row" key={d.id}>
                        <div>
                          <strong>{d.name}</strong>
                          <small>{d.summary || d.subtype || "No summary"}</small>
                          {d.sourceType === "srd_api" && (
                            <span className="source-badge">SRD {d.sourceRuleset}</span>
                          )}
                        </div>
                        <span>{d.category.replaceAll("_", " ")}</span>
                        <span>{d.rarity || "—"}</span>
                        <span>{d.rulesVersion || "—"}</span>
                        <div className="row-actions">
                          <button
                            className="text-btn"
                            onClick={() => {
                              setSelectedDefId(d.id);
                              setSelectedInstanceId("");
                              setModal("itemInstance");
                            }}
                          >
                            Place in World <ChevronRight size={14} />
                          </button>
                          <button
                            className="icon-btn"
                            title="Edit definition"
                            onClick={() => {
                              setSelectedDefId(d.id);
                              setModal("itemDef");
                            }}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            className="icon-btn danger-icon"
                            title="Delete definition"
                            onClick={() => deleteItemDefinition(d.id)}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {view === "creaturemaster" && (
              <CreatureMaster
                creatures={data.creatureDefinitions}
                onAdd={(creature) =>
                  patch((draft) => {
                    draft.creatureDefinitions.push(creature);
                  })
                }
                onUpdate={(creature) =>
                  patch((draft) => {
                    const index = draft.creatureDefinitions.findIndex(
                      (entry) => entry.id === creature.id,
                    );
                    if (index >= 0) {
                      draft.creatureDefinitions[index] = creature;
                    }
                  })
                }
                onDelete={(id) =>
                  patch((draft) => {
                    draft.creatureDefinitions = draft.creatureDefinitions.filter(
                      (creature) => creature.id !== id,
                    );
                  })
                }
                newId={newId}
                now={now}
              />
            )}

            {view === "items" && (
              <section>
                <SectionHead
                  title="World Items"
                  subtitle="Specific item instances that physically exist in this world. Ownership, possession, storage and condition can change over time."
                  onAdd={() => {
                    setSelectedInstanceId("");
                    setSelectedDefId("");
                    setModal("itemInstance");
                  }}
                />
                {!worldInstances.length ? (
                  <Empty />
                ) : (
                  <div className="card-grid">
                    {worldInstances.map((i) => {
                      const d = defMap[i.definitionId];
                      const holder = entityMap[i.currentHolderEntityId || ""];
                      const loc = entityMap[i.currentLocationEntityId || ""];
                      return (
                        <article className="entity-card item-instance-card" key={i.id}>
                          <div className="entity-top">
                            <span>{d?.category?.replaceAll("_", " ") || "ITEM"}</span>
                            <span>{i.uniqueItem ? "UNIQUE" : `QTY ${i.quantity}`}</span>
                          </div>
                          <h3>{i.displayName || d?.name || "Unknown Item"}</h3>
                          <p>{d?.summary || "No item summary."}</p>
                          <div className="item-facts">
                            <span>
                              <b>Condition</b>
                              {i.condition}
                            </span>
                            <span>
                              <b>Identified</b>
                              {i.identified ? "Yes" : "No"}
                            </span>
                            <span>
                              <b>Holder</b>
                              {holder?.name || "—"}
                            </span>
                            <span>
                              <b>Location</b>
                              {loc?.name || "—"}
                            </span>
                          </div>
                          <div className="card-actions">
                            <button
                              className="text-btn"
                              onClick={() => {
                                setSelectedInstanceId(i.id);
                                setModal("moveInstance");
                              }}
                            >
                              Move / transfer <ChevronRight size={14} />
                            </button>
                            <button
                              className="secondary compact"
                              onClick={() => {
                                setSelectedInstanceId(i.id);
                                setSelectedDefId(i.definitionId);
                                setModal("itemInstance");
                              }}
                            >
                              <Pencil size={14} />
                              Edit
                            </button>
                            <button
                              className="danger compact"
                              onClick={() => deleteItemInstance(i.id)}
                            >
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </section>
            )}

            {view === "relationships" && (
              <section>
                <SectionHead
                  title="Relationships"
                  subtitle="Connect people, places, factions, items and lore into a living world graph."
                  onAdd={() => {
                    setSelectedRelationshipId("");
                    setModal("relationship");
                  }}
                />
                {!data.relationships.filter((r) => r.worldId === worldId).length ? (
                  <Empty />
                ) : (
                  <div className="relationship-list">
                    {data.relationships
                      .filter((r) => r.worldId === worldId)
                      .map((r) => (
                        <div className="relationship-row" key={r.id}>
                          <strong>{entityMap[r.sourceId]?.name}</strong>
                          <span className="relation">{r.type.replaceAll("_", " ")}</span>
                          <strong>{entityMap[r.targetId]?.name}</strong>
                          {r.secret && <span className="secret-badge">GM SECRET</span>}
                          <div className="row-actions">
                            <button
                              className="icon-btn"
                              title="Edit relationship"
                              onClick={() => {
                                setSelectedRelationshipId(r.id);
                                setModal("relationship");
                              }}
                            >
                              <Pencil size={14} />
                            </button>
                            <button
                              className="icon-btn danger-icon"
                              title="Delete relationship"
                              onClick={() => {
                                if (window.confirm("Delete this relationship?"))
                                  patch((d) => {
                                    d.relationships = d.relationships.filter((x) => x.id !== r.id);
                                  });
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                          <p>{r.description}</p>
                        </div>
                      ))}
                  </div>
                )}
              </section>
            )}
            {view === "maps" && (
              <section>
                <SectionHead
                  title="Maps"
                  subtitle="Organize GM, player and VTT variants without losing their connection to locations."
                  onAdd={() => {
                    setSelectedMapId("");
                    setModal("map");
                  }}
                />
                {!data.maps.filter((m) => m.worldId === worldId).length ? (
                  <Empty />
                ) : (
                  <div className="card-grid">
                    {data.maps
                      .filter((m) => m.worldId === worldId)
                      .map((m) => (
                        <article className="entity-card" key={m.id}>
                          <div className="entity-top">
                            <span>{m.mapType}</span>
                          </div>
                          <h3>{m.name}</h3>
                          <p>{m.notes || "No notes."}</p>
                          <div className="map-variants">
                            <span className={m.gmUrl ? "ready" : ""}>GM</span>
                            <span className={m.playerUrl ? "ready" : ""}>PLAYER</span>
                            <span className={m.vttUrl ? "ready" : ""}>VTT</span>
                          </div>
                          <div className="card-actions">
                            <button
                              className="secondary compact"
                              onClick={() => {
                                setSelectedMapId(m.id);
                                setModal("map");
                              }}
                            >
                              <Pencil size={14} />
                              Edit
                            </button>
                            <button className="danger compact" onClick={() => deleteMap(m.id)}>
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        </article>
                      ))}
                  </div>
                )}
              </section>
            )}
            {view === "campaigns" && (
              <section>
                <SectionHead
                  title="Campaigns"
                  subtitle="Campaigns consume world data without changing canonical truth."
                  onAdd={() => {
                    setSelectedCampaignId("");
                    setModal("campaign");
                  }}
                />
                {!data.campaigns.filter((c) => c.worldId === worldId).length ? (
                  <Empty />
                ) : (
                  <div className="card-grid">
                    {data.campaigns
                      .filter((c) => c.worldId === worldId)
                      .map((c) => (
                        <article className="entity-card" key={c.id}>
                          <div className="entity-top">
                            <span>CAMPAIGN</span>
                          </div>
                          <h3>{c.name}</h3>
                          <p>{c.description}</p>
                          <div className="sync-note">Shared-data ready for Storywarden</div>
                          <div className="card-actions">
                            <button
                              className="secondary compact"
                              onClick={() => {
                                setSelectedCampaignId(c.id);
                                setModal("campaign");
                              }}
                            >
                              <Pencil size={14} />
                              Edit
                            </button>
                            <button className="danger compact" onClick={() => deleteCampaign(c.id)}>
                              <Trash2 size={14} />
                              Delete
                            </button>
                          </div>
                        </article>
                      ))}
                  </div>
                )}
              </section>
            )}
          </>
        )}
      </main>

      {modal === "world" && (
        <Modal
          title={selectedWorld ? "Edit World" : "Create World"}
          onClose={() => {
            setSelectedWorldId("");
            setModal(null);
          }}
        >
          <form
            key={selectedWorldId || "new"}
            onSubmit={(e) => {
              e.preventDefault();
              selectedWorldId ? updateWorld(e.currentTarget) : addWorld(e.currentTarget);
            }}
          >
            <Field l="World name">
              <input name="name" required defaultValue={selectedWorld?.name || ""} />
            </Field>
            <div className="form-grid">
              <Field l="Theme">
                <input name="theme" defaultValue={selectedWorld?.theme || ""} />
              </Field>
              <Field l="Magic level">
                <input name="magic" defaultValue={selectedWorld?.magicLevel || ""} />
              </Field>
            </div>
            <Field l="System">
              <input name="system" defaultValue={selectedWorld?.system || "D&D 5e / 2024"} />
            </Field>
            <Field l="Description">
              <textarea name="description" defaultValue={selectedWorld?.description || ""} />
            </Field>
            {selectedWorld && (
              <div className="danger-zone">
                <strong>Danger zone</strong>
                <p>World deletion is only allowed when no world records remain.</p>
                <button
                  type="button"
                  className="danger"
                  onClick={() => deleteWorld(selectedWorld.id)}
                >
                  <Trash2 size={14} />
                  Delete World
                </button>
              </div>
            )}
            <Actions
              close={() => {
                setSelectedWorldId("");
                setModal(null);
              }}
              text={selectedWorld ? "Save Changes" : "Create World"}
            />
          </form>
        </Modal>
      )}

      {modal === "entity" && (
        <Modal
          title={`${selectedEntity ? "Edit" : "Add"} ${entityLabel(preset)}`}
          onClose={() => {
            setSelectedEntityId("");
            setModal(null);
          }}
        >
          <form
            key={selectedEntityId || "new"}
            onSubmit={(e) => {
              e.preventDefault();
              selectedEntityId ? updateEntity(e.currentTarget) : addEntity(e.currentTarget);
            }}
          >
            <input type="hidden" name="type" value={preset} />
            <Field l="Name">
              <input name="name" required defaultValue={selectedEntity?.name || ""} />
            </Field>
            <Field l="Description">
              <textarea name="description" defaultValue={selectedEntity?.description || ""} />
            </Field>
            <div className="form-grid">
              <Field l={fields(preset)[0]}>
                <input
                  name="a"
                  defaultValue={String(entityDetail(selectedEntity, preset, 0) || "")}
                />
              </Field>
              <Field l={fields(preset)[1]}>
                <input
                  name="b"
                  defaultValue={String(entityDetail(selectedEntity, preset, 1) || "")}
                />
              </Field>
            </div>
            <Field l="Tags">
              <input
                name="tags"
                placeholder="relic, political, recurring"
                defaultValue={selectedEntity?.tags.join(", ") || ""}
              />
            </Field>
            <Field l="Visibility">
              <select name="visibility" defaultValue={selectedEntity?.visibility || "gm_only"}>
                <option value="gm_only">GM only</option>
                <option value="campaign_known">Campaign known</option>
                <option value="public">Public</option>
              </select>
            </Field>
            <Actions
              close={() => {
                setSelectedEntityId("");
                setModal(null);
              }}
              text={selectedEntity ? "Save Changes" : "Save"}
            />
          </form>
        </Modal>
      )}

      {modal === "itemDef" && (
        <Modal
          title={selectedDef ? "Edit Item Definition" : "Create Item Definition"}
          onClose={() => {
            setSelectedDefId("");
            setModal(null);
          }}
        >
          <form
            key={selectedDefId || "new"}
            onSubmit={(e) => {
              e.preventDefault();
              selectedDefId
                ? updateItemDefinition(e.currentTarget)
                : addItemDefinition(e.currentTarget);
            }}
          >
            <Field l="Item name">
              <input
                name="name"
                required
                placeholder="Oathshard"
                defaultValue={selectedDef?.name || ""}
              />
            </Field>
            <div className="form-grid">
              <Field l="Category">
                <select name="category" defaultValue={selectedDef?.category || "weapon"}>
                  <option value="weapon">Weapon</option>
                  <option value="armor">Armor</option>
                  <option value="wondrous_item">Wondrous Item</option>
                  <option value="potion">Potion</option>
                  <option value="scroll">Scroll</option>
                  <option value="ring">Ring</option>
                  <option value="rod">Rod</option>
                  <option value="staff">Staff</option>
                  <option value="wand">Wand</option>
                  <option value="tool">Tool</option>
                  <option value="treasure">Treasure</option>
                  <option value="mundane">Mundane</option>
                  <option value="other">Other</option>
                </select>
              </Field>
              <Field l="Subtype">
                <input
                  name="subtype"
                  placeholder="Longsword, cloak, key..."
                  defaultValue={selectedDef?.subtype || ""}
                />
              </Field>
            </div>
            <div className="form-grid">
              <Field l="Rarity">
                <select name="rarity" defaultValue={selectedDef?.rarity || ""}>
                  <option value="">None</option>
                  <option>Common</option>
                  <option>Uncommon</option>
                  <option>Rare</option>
                  <option>Very Rare</option>
                  <option>Legendary</option>
                  <option>Artifact</option>
                </select>
              </Field>
              <Field l="Rules version">
                <input
                  name="rulesVersion"
                  defaultValue={selectedDef?.rulesVersion || "D&D 5e / 2024"}
                />
              </Field>
            </div>
            <div className="check-grid">
              <label className="check">
                <input
                  type="checkbox"
                  name="magical"
                  defaultChecked={selectedDef?.magical || false}
                />
                Magical
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  name="attune"
                  defaultChecked={selectedDef?.requiresAttunement || false}
                />
                Requires attunement
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  name="consumable"
                  defaultChecked={selectedDef?.consumable || false}
                />
                Consumable
              </label>
            </div>
            <Field l="Attunement requirements">
              <input
                name="attunementRequirements"
                placeholder="e.g. by a paladin"
                defaultValue={selectedDef?.attunementRequirements || ""}
              />
            </Field>
            <div className="form-grid">
              <Field l="Charges">
                <input
                  type="number"
                  min="0"
                  name="charges"
                  defaultValue={selectedDef?.charges ?? ""}
                />
              </Field>
              <Field l="Weight (lb.)">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  name="weight"
                  defaultValue={selectedDef?.weight ?? ""}
                />
              </Field>
            </div>
            <div className="form-grid">
              <Field l="Value (gp)">
                <input
                  type="number"
                  min="0"
                  name="valueGp"
                  defaultValue={selectedDef?.valueGp ?? ""}
                />
              </Field>
              <Field l="Source / inspiration">
                <input
                  name="source"
                  placeholder="Homebrew, DMG, campaign note..."
                  defaultValue={selectedDef?.source || ""}
                />
              </Field>
            </div>
            <Field l="Player-facing summary">
              <textarea name="summary" defaultValue={selectedDef?.summary || ""} />
            </Field>
            <Field l="Game mechanics">
              <textarea
                name="mechanics"
                placeholder="+1 longsword; special abilities; charges; saves..."
                defaultValue={selectedDef?.mechanics || ""}
              />
            </Field>
            <Field l="Lore">
              <textarea
                name="lore"
                placeholder="History, maker, cultural significance..."
                defaultValue={selectedDef?.lore || ""}
              />
            </Field>
            <Field l="GM secrets">
              <textarea
                name="gmSecrets"
                placeholder="Curse, hidden powers, true origin..."
                defaultValue={selectedDef?.gmSecrets || ""}
              />
            </Field>
            <Field l="Image URL">
              <input name="imageUrl" defaultValue={selectedDef?.imageUrl || ""} />
            </Field>
            <Field l="Tags">
              <input
                name="tags"
                placeholder="shardbound, sword, artifact"
                defaultValue={selectedDef?.tags.join(", ") || ""}
              />
            </Field>
            <Actions
              close={() => {
                setSelectedDefId("");
                setModal(null);
              }}
              text={selectedDef ? "Save Changes" : "Save Definition"}
            />
          </form>
        </Modal>
      )}

      {modal === "itemInstance" && (
        <Modal
          title={selectedInstance ? "Edit World Item" : "Place Item in World"}
          onClose={() => {
            setSelectedInstanceId("");
            setModal(null);
          }}
        >
          <form
            key={selectedInstanceId || "new"}
            onSubmit={(e) => {
              e.preventDefault();
              selectedInstanceId
                ? updateItemInstance(e.currentTarget)
                : addItemInstance(e.currentTarget);
            }}
          >
            <Field l="Item definition">
              <select
                name="definitionId"
                required
                defaultValue={selectedInstance?.definitionId || selectedDefId}
              >
                {data.itemDefinitions.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} · {d.rarity || d.category}
                  </option>
                ))}
              </select>
            </Field>
            <Field l="Instance name / alias">
              <input
                name="displayName"
                placeholder="Optional; useful for unique named copies"
                defaultValue={selectedInstance?.displayName || ""}
              />
            </Field>
            <div className="form-grid">
              <Field l="Quantity">
                <input
                  name="quantity"
                  type="number"
                  min="1"
                  defaultValue={selectedInstance?.quantity || 1}
                />
              </Field>
              <Field l="Condition">
                <select name="condition" defaultValue={selectedInstance?.condition || "Good"}>
                  <option>Good</option>
                  <option>Worn</option>
                  <option>Damaged</option>
                  <option>Broken</option>
                </select>
              </Field>
            </div>
            <div className="check-grid">
              <label className="check">
                <input
                  type="checkbox"
                  name="uniqueItem"
                  defaultChecked={selectedInstance?.uniqueItem || false}
                />
                Unique item
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  name="identified"
                  defaultChecked={selectedInstance?.identified || false}
                />
                Identified
              </label>
            </div>
            <Field l="Owner">
              <select name="owner" defaultValue={selectedInstance?.currentOwnerEntityId || ""}>
                <option value="">None / unknown</option>
                {ents
                  .filter((e) => ["npc", "faction", "location"].includes(e.type))
                  .map((e) => (
                    <option value={e.id} key={e.id}>
                      {e.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field l="Current holder">
              <select name="holder" defaultValue={selectedInstance?.currentHolderEntityId || ""}>
                <option value="">None</option>
                {ents
                  .filter((e) => ["npc", "faction"].includes(e.type))
                  .map((e) => (
                    <option value={e.id} key={e.id}>
                      {e.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field l="Current location / storage">
              <select
                name="location"
                defaultValue={selectedInstance?.currentLocationEntityId || ""}
              >
                <option value="">None / unknown</option>
                {ents
                  .filter((e) => e.type === "location")
                  .map((e) => (
                    <option value={e.id} key={e.id}>
                      {e.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field l="Instance notes">
              <textarea name="notes" defaultValue={selectedInstance?.notes || ""} />
            </Field>
            <Actions
              close={() => {
                setSelectedInstanceId("");
                setModal(null);
              }}
              text={selectedInstance ? "Save Changes" : "Place Item"}
            />
          </form>
        </Modal>
      )}

      {modal === "moveInstance" && (
        <Modal title="Move / Transfer World Item" onClose={() => setModal(null)}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              moveInstance(e.currentTarget);
            }}
          >
            <Field l="Item instance">
              <select name="instanceId" required defaultValue={selectedInstanceId}>
                {worldInstances.map((i) => (
                  <option value={i.id} key={i.id}>
                    {i.displayName || defMap[i.definitionId]?.name || "Unknown Item"}
                  </option>
                ))}
              </select>
            </Field>
            <Field l="Campaign">
              <select name="campaign">
                <option value="">Canonical / prep change</option>
                {data.campaigns
                  .filter((c) => c.worldId === worldId)
                  .map((c) => (
                    <option value={c.id} key={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field l="Action">
              <select name="kind">
                <option>stored</option>
                <option>given</option>
                <option>loaned</option>
                <option>sold</option>
                <option>stolen</option>
                <option>recovered</option>
                <option>found</option>
                <option>destroyed</option>
              </select>
            </Field>
            <Field l="Destination / holder">
              <select name="to">
                <option value="">Unknown / nowhere</option>
                {ents
                  .filter((e) => ["npc", "location", "faction"].includes(e.type))
                  .map((e) => (
                    <option value={e.id} key={e.id}>
                      {e.name} · {e.type}
                    </option>
                  ))}
              </select>
            </Field>
            <Field l="Notes">
              <textarea name="notes" />
            </Field>
            <Actions close={() => setModal(null)} text="Record Movement" />
          </form>
        </Modal>
      )}

      {modal === "relationship" && (
        <Modal
          title={selectedRelationship ? "Edit Relationship" : "Add Relationship"}
          onClose={() => {
            setSelectedRelationshipId("");
            setModal(null);
          }}
        >
          <form
            key={selectedRelationshipId || "new"}
            onSubmit={(e) => {
              e.preventDefault();
              selectedRelationshipId
                ? updateRelationship(e.currentTarget)
                : addRelationship(e.currentTarget);
            }}
          >
            <Field l="From">
              <select name="source" defaultValue={selectedRelationship?.sourceId || ""}>
                {ents.map((e) => (
                  <option value={e.id} key={e.id}>
                    {e.name} · {e.type}
                  </option>
                ))}
              </select>
            </Field>
            <Field l="Relationship">
              <input
                name="type"
                placeholder="MEMBER_OF, LOCATED_AT, OWNS"
                required
                defaultValue={selectedRelationship?.type.replaceAll("_", " ") || ""}
              />
            </Field>
            <Field l="To">
              <select name="target" defaultValue={selectedRelationship?.targetId || ""}>
                {ents.map((e) => (
                  <option value={e.id} key={e.id}>
                    {e.name} · {e.type}
                  </option>
                ))}
              </select>
            </Field>
            <Field l="Description">
              <textarea name="description" defaultValue={selectedRelationship?.description || ""} />
            </Field>
            <label className="check">
              <input
                type="checkbox"
                name="secret"
                defaultChecked={selectedRelationship?.secret || false}
              />
              GM-secret relationship
            </label>
            <Actions
              close={() => {
                setSelectedRelationshipId("");
                setModal(null);
              }}
              text={selectedRelationship ? "Save Changes" : "Create"}
            />
          </form>
        </Modal>
      )}
      {modal === "campaign" && (
        <Modal
          title={selectedCampaign ? "Edit Campaign" : "Create Campaign"}
          onClose={() => {
            setSelectedCampaignId("");
            setModal(null);
          }}
        >
          <form
            key={selectedCampaignId || "new"}
            onSubmit={(e) => {
              e.preventDefault();
              selectedCampaignId ? updateCampaign(e.currentTarget) : addCampaign(e.currentTarget);
            }}
          >
            <Field l="Campaign name">
              <input name="name" required defaultValue={selectedCampaign?.name || ""} />
            </Field>
            <Field l="Premise">
              <textarea name="description" defaultValue={selectedCampaign?.description || ""} />
            </Field>
            <div className="notice">
              Campaign state and events remain separate from canonical world truth.
            </div>
            <Actions
              close={() => {
                setSelectedCampaignId("");
                setModal(null);
              }}
              text={selectedCampaign ? "Save Changes" : "Create Campaign"}
            />
          </form>
        </Modal>
      )}
      {modal === "srdImport" && (
        <SrdItemImporter
          onClose={() => setModal(null)}
          newId={newId}
          now={now}
          onImport={(definition) => {
            patch((d) => d.itemDefinitions.push(definition));
            setSelectedDefId(definition.id);
            setModal(null);
          }}
        />
      )}
      {modal === "map" && (
        <Modal
          title={selectedMap ? "Edit Map" : "Add Map"}
          onClose={() => {
            setSelectedMapId("");
            setModal(null);
          }}
        >
          <form
            key={selectedMapId || "new"}
            onSubmit={(e) => {
              e.preventDefault();
              selectedMapId ? updateMap(e.currentTarget) : addMap(e.currentTarget);
            }}
          >
            <Field l="Map name">
              <input name="name" required defaultValue={selectedMap?.name || ""} />
            </Field>
            <div className="form-grid">
              <Field l="Map type">
                <select name="mapType" defaultValue={selectedMap?.mapType || "World Map"}>
                  <option>World Map</option>
                  <option>Region Map</option>
                  <option>Town / City Map</option>
                  <option>Dungeon Map</option>
                  <option>Battle Map</option>
                  <option>Building / Interior</option>
                </select>
              </Field>
              <Field l="Linked entity">
                <select name="linked" defaultValue={selectedMap?.linkedEntityId || ""}>
                  <option value="">None</option>
                  {ents.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            <div className="form-grid">
              <Field l="Grid type">
                <select name="gridType" defaultValue={selectedMap?.gridType || ""}>
                  <option value="">None</option>
                  <option>Square</option>
                  <option>Hex</option>
                </select>
              </Field>
              <Field l="Grid size (px)">
                <input type="number" name="gridSize" defaultValue={selectedMap?.gridSize ?? ""} />
              </Field>
            </div>
            <Field l="GM map URL">
              <input name="gmUrl" defaultValue={selectedMap?.gmUrl || ""} />
            </Field>
            <Field l="Player map URL">
              <input name="playerUrl" defaultValue={selectedMap?.playerUrl || ""} />
            </Field>
            <Field l="VTT / clean map URL">
              <input name="vttUrl" defaultValue={selectedMap?.vttUrl || ""} />
            </Field>
            <Field l="Notes">
              <textarea name="notes" defaultValue={selectedMap?.notes || ""} />
            </Field>
            <Actions
              close={() => {
                setSelectedMapId("");
                setModal(null);
              }}
              text={selectedMap ? "Save Changes" : "Save Map"}
            />
          </form>
        </Modal>
      )}
    </div>
  );
}

function Welcome({ onCreate, onDemo }: { onCreate: () => void; onDemo: () => void }) {
  return (
    <section className="welcome">
      <div className="welcome-card">
        <div className="star">✦</div>
        <h2>Worldwarden</h2>
        <p>A browser-based Starfall Works worldbuilding system for D&D 5e / 2024.</p>
        <div className="welcome-actions">
          <button className="primary" onClick={onCreate}>
            Create Your First World
          </button>
          <button className="secondary" onClick={onDemo}>
            Explore Demo World
          </button>
        </div>
      </div>
    </section>
  );
}
function Overview({ data, worldId }: { data: AppData; worldId: string }) {
  const es = data.entities.filter((e) => e.worldId === worldId),
    w = data.worlds.find((x) => x.id === worldId)!;
  const count = (t: string) => es.filter((e) => e.type === t).length;
  return (
    <>
      <div className="stat-grid">
        {[
          ["NPCs", count("npc"), Users],
          ["Locations", count("location"), Landmark],
          ["Factions", count("faction"), Shield],
          ["Item Defs", data.itemDefinitions.length, LibraryBig],
          ["World Items", data.itemInstances.filter((i) => i.worldId === worldId).length, Gem],
          ["Maps", data.maps.filter((m) => m.worldId === worldId).length, Map],
        ].map(([l, n, I]: any) => (
          <div className="stat" key={l}>
            <I size={20} />
            <div>
              <strong>{n}</strong>
              <span>{l}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="overview-grid">
        <section className="panel">
          <div className="eyebrow">WORLD PROFILE</div>
          <h2>Canonical truth</h2>
          <dl className="facts">
            <div>
              <dt>System</dt>
              <dd>{w.system}</dd>
            </div>
            <div>
              <dt>Theme</dt>
              <dd>{w.theme || "—"}</dd>
            </div>
            <div>
              <dt>Magic</dt>
              <dd>{w.magicLevel || "—"}</dd>
            </div>
          </dl>
          <p className="body-copy">{w.description}</p>
        </section>
        <section className="panel">
          <div className="eyebrow">SHARED PLATFORM</div>
          <h2>Worldwarden ↔ Storywarden</h2>
          <div className="flow">
            <div>
              <b>Worldwarden</b>
              <span>Build lore</span>
            </div>
            <ChevronRight />
            <div>
              <b>Shared data</b>
              <span>Definitions + instances + events</span>
            </div>
            <ChevronRight />
            <div>
              <b>Storywarden</b>
              <span>Run campaigns</span>
            </div>
          </div>
          <p className="body-copy">
            Item definitions stay reusable while campaign-specific possession and storage can change
            safely.
          </p>
        </section>
      </div>
    </>
  );
}
function SectionHead({
  title,
  subtitle,
  onAdd,
}: {
  title: string;
  subtitle: string;
  onAdd: () => void;
}) {
  return (
    <div className="section-head">
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
      <button className="primary" onClick={onAdd}>
        <Plus size={16} />
        Add
      </button>
    </div>
  );
}
function Empty() {
  return (
    <div className="empty">
      <Castle size={38} />
      <h3>Nothing here yet</h3>
      <p>Add the first record to this world.</p>
    </div>
  );
}
function Field({ l, children }: { l: string; children: React.ReactNode }) {
  return (
    <label className="field">
      <span>{l}</span>
      {children}
    </label>
  );
}
function Actions({ close, text }: { close: () => void; text: string }) {
  return (
    <div className="form-actions">
      <button type="button" className="secondary" onClick={close}>
        Cancel
      </button>
      <button type="submit" className="primary">
        {text}
      </button>
    </div>
  );
}
function fields(t: EntityType): [string, string] {
  return t === "npc"
    ? ["Species / ancestry", "Occupation / role"]
    : t === "location"
      ? ["Location type", "Terrain / environment"]
      : t === "faction"
        ? ["Faction type", "Purpose"]
        : t === "pantheon"
          ? ["Creation myth", "Cosmology"]
          : t === "deity"
            ? ["Portfolio / domains", "Symbol"]
            : t === "rumor"
              ? ["Truth status", "Truth explanation"]
              : t === "secret"
                ? ["Secret type", "Importance (1–5)"]
                : ["Adventure type", "Objective"];
}
function entityDetail(e: any, t: EntityType, index: 0 | 1) {
  if (!e) return "";
  const keys =
    t === "npc"
      ? ["species", "occupation"]
      : t === "location"
        ? ["locationType", "terrain"]
        : t === "faction"
          ? ["factionType", "purpose"]
          : t === "pantheon"
            ? ["creationMyth", "cosmology"]
            : t === "deity"
              ? ["portfolio", "symbol"]
              : t === "rumor"
                ? ["truthStatus", "truthExplanation"]
                : t === "secret"
                  ? ["secretType", "importance"]
                  : ["adventureType", "objective"];
  return e.details?.[keys[index]] ?? "";
}
